import React, { useState } from 'react';
import { Monitor } from 'lucide-react';
import { DisplayAdsContent } from '../types/ads.types';
import { COMMON_AD_SIZES, AD_PLATFORMS } from '../config/adPlatformConfig';
import MediaUploader from '../components/MediaUploader';
import PlatformEditorTabs from '../components/PlatformEditorTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface DisplayNetworkEditorProps {
  content: DisplayAdsContent;
  isEditing: boolean;
  onChange: (field: keyof DisplayAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function DisplayNetworkEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: DisplayNetworkEditorProps) {
  const platform = AD_PLATFORMS.display;
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [currentCtaIndex, setCurrentCtaIndex] = useState(0);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [showAllCtas, setShowAllCtas] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetTab, setActiveAssetTab] = useState('images');
  const [selectedAdFormats, setSelectedAdFormats] = useState<string[]>(
    content.adFormats || ['banner', 'responsive']
  );

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  // Initialize variations if they don't exist
  const headlineVariations = content.headlineVariations || [content.headline || ''];
  const ctaVariations = content.callToActionVariations || ['Learn More', 'Get Started', 'Shop Now'];

  const updateHeadlineVariations = (variations: string[]) => {
    onChange('headlineVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('headline', variations[currentHeadlineIndex] || '');
  };

  const updateCtaVariations = (variations: string[]) => {
    onChange('callToActionVariations', variations);
  };

  const getAssetSubTabs = () => {
    const tabs = [];

    // Always show text for headlines and CTAs
    tabs.push({ id: 'text', label: 'Text' });

    // Images for all display formats
    tabs.push({ id: 'images', label: 'Images' });

    // HTML5 for rich media
    if (selectedAdFormats.includes('rich_media') || selectedAdFormats.includes('html5')) {
      tabs.push({ id: 'html5', label: 'HTML5' });
    }

    // Videos for video display ads
    if (selectedAdFormats.includes('video')) {
      tabs.push({ id: 'videos', label: 'Videos' });
    }

    return tabs.length > 0 ? tabs : [{ id: 'text', label: 'Text' }];
  };

  const handleAdFormatToggle = (format: string) => {
    const newFormats = selectedAdFormats.includes(format)
      ? selectedAdFormats.filter(f => f !== format)
      : [...selectedAdFormats, format];

    setSelectedAdFormats(newFormats);
    onChange('adFormats', newFormats);

    // Update activeAssetTab if current one is no longer valid
    const validTabs = getAssetSubTabs();
    if (!validTabs.find(t => t.id === activeAssetTab)) {
      setActiveAssetTab(validTabs[0].id);
    }
  };

  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Monitor className="w-5 h-5 mr-2 text-indigo-600" />
            Display & Programmatic Campaign Settings
          </h3>

          <div className="space-y-4">
            {/* Campaign Name - Full Width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                value={content.campaignName || ''}
                onChange={(e) => onChange('campaignName', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Enter campaign name..."
              />
            </div>

            {/* DSP Platform - Full Width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">DSP Platform</label>
              <select
                value={content.dsp || 'google_display'}
                onChange={(e) => onChange('dsp', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="google_display">Google Display Network</option>
                <option value="amazon_dsp">Amazon DSP</option>
                <option value="trade_desk">The Trade Desk</option>
                <option value="adobe">Adobe Advertising Cloud</option>
                <option value="dv360">Display & Video 360</option>
              </select>
            </div>

            {/* Ad Formats - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ad Formats</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAdFormats.includes('banner')}
                    onChange={() => handleAdFormatToggle('banner')}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-sm text-gray-700">Static Banner</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAdFormats.includes('responsive')}
                    onChange={() => handleAdFormatToggle('responsive')}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-sm text-gray-700">Responsive Display</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAdFormats.includes('html5')}
                    onChange={() => handleAdFormatToggle('html5')}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-sm text-gray-700">HTML5 / Rich Media</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAdFormats.includes('video')}
                    onChange={() => handleAdFormatToggle('video')}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-sm text-gray-700">Video Display</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAdFormats.includes('native')}
                    onChange={() => handleAdFormatToggle('native')}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-sm text-gray-700">Native Ads</span>
                </label>
              </div>
            </div>

            {/* Ad Sizes - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ad Sizes</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(COMMON_AD_SIZES.display).map(([name, size]) => (
                  <label key={size} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content.adSizes?.includes(size) || false}
                      onChange={(e) => {
                        const sizes = content.adSizes || [];
                        if (e.target.checked) {
                          onChange('adSizes', [...sizes, size]);
                        } else {
                          onChange('adSizes', sizes.filter(s => s !== size));
                        }
                      }}
                      disabled={!isEditing}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    <span className="ml-2 text-sm">{name} ({size})</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-6">
          {/* Asset Sub-tabs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b">
              <div className="flex space-x-1 p-1">
                {getAssetSubTabs().map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveAssetTab(tab.id)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeAssetTab === tab.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Text Assets */}
              {activeAssetTab === 'text' && (
                <div className="space-y-6">
                  {/* Headlines */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Headlines</label>
                    <input
                      type="text"
                      value={content.headline || ''}
                      onChange={(e) => onChange('headline', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      maxLength={50}
                      placeholder="Enter headline (up to 50 characters)..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{(content.headline || '').length}/50 characters</div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={content.description || ''}
                      onChange={(e) => onChange('description', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      rows={3}
                      maxLength={90}
                      placeholder="Enter description (up to 90 characters)..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{(content.description || '').length}/90 characters</div>
                  </div>

                  {/* Call to Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Call to Action</label>
                    <select
                      value={content.callToAction || 'learn_more'}
                      onChange={(e) => onChange('callToAction', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="learn_more">Learn More</option>
                      <option value="shop_now">Shop Now</option>
                      <option value="get_started">Get Started</option>
                      <option value="sign_up">Sign Up</option>
                      <option value="download">Download</option>
                      <option value="contact_us">Contact Us</option>
                      <option value="book_now">Book Now</option>
                      <option value="watch_video">Watch Video</option>
                    </select>
                  </div>

                  {/* Business Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={content.businessName || ''}
                      onChange={(e) => onChange('businessName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="Your business name..."
                    />
                  </div>
                </div>
              )}

              {/* Images */}
              {activeAssetTab === 'images' && (
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'image') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'image') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Display"
                  acceptedTypes={['image/*']}
                  maxFiles={20}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              )}

              {/* HTML5 */}
              {activeAssetTab === 'html5' && (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Upload HTML5 creative packages (ZIP files containing HTML, CSS, JS, and assets)
                  </div>
                  <MediaUploader
                    creatives={content.creatives?.filter(c => c.type === 'html5') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.creatives?.filter(c => c.type !== 'html5') || [];
                      onChange('creatives', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="Display"
                    acceptedTypes={['.zip', 'application/zip']}
                    maxFiles={10}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />
                </div>
              )}

              {/* Videos */}
              {activeAssetTab === 'videos' && (
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'video') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'video') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="Display"
                  acceptedTypes={['video/*']}
                  maxFiles={5}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Placement & Targeting</h3>

          <div className="space-y-4">
            {/* Targeting Strategy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Targeting Strategy</label>
              <select
                value={content.targetingStrategy || 'contextual'}
                onChange={(e) => onChange('targetingStrategy', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="contextual">Contextual Targeting</option>
                <option value="behavioral">Behavioral Targeting</option>
                <option value="retargeting">Retargeting / Remarketing</option>
                <option value="lookalike">Lookalike Audiences</option>
                <option value="demographic">Demographic Targeting</option>
                <option value="geographic">Geographic Targeting</option>
              </select>
            </div>

            {/* Placement Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Placement Type</label>
              <select
                value={content.placementType || 'automatic'}
                onChange={(e) => onChange('placementType', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="automatic">Automatic Placements</option>
                <option value="managed">Managed Placements</option>
                <option value="whitelist">Whitelist Only</option>
                <option value="exclusions">With Exclusions</option>
              </select>
            </div>

            {/* Audience Segments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Audience Segments</label>
              <input
                type="text"
                value={content.audienceSegments || ''}
                onChange={(e) => onChange('audienceSegments', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., In-market for travel, Tech enthusiasts, Parents"
              />
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contextual Keywords</label>
              <input
                type="text"
                value={content.keywords || ''}
                onChange={(e) => onChange('keywords', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Enter keywords separated by commas..."
              />
            </div>

            {/* Frequency Capping */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency Capping</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={content.frequencyCap || ''}
                  onChange={(e) => onChange('frequencyCap', parseInt(e.target.value) || 0)}
                  disabled={!isEditing}
                  className="w-20 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  min="1"
                  placeholder="3"
                />
                <span className="text-sm text-gray-600">impressions per</span>
                <select
                  value={content.frequencyPeriod || 'day'}
                  onChange={(e) => onChange('frequencyPeriod', e.target.value)}
                  disabled={!isEditing}
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Budget & Performance</h3>

          <div className="space-y-4">
            {/* Budget Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Type</label>
              <select
                value={content.budgetType || 'daily'}
                onChange={(e) => onChange('budgetType', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="daily">Daily Budget</option>
                <option value="monthly">Monthly Budget</option>
                <option value="total">Total Campaign Budget</option>
              </select>
            </div>

            {/* Budget Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount ($)</label>
              <input
                type="number"
                value={content.budget || ''}
                onChange={(e) => onChange('budget', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="10"
                step="10"
                placeholder="Enter budget amount"
              />
            </div>

            {/* Bid Strategy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bid Strategy</label>
              <select
                value={content.bidStrategy || 'auto'}
                onChange={(e) => onChange('bidStrategy', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="auto">Automated Bidding</option>
                <option value="manual_cpm">Manual CPM</option>
                <option value="manual_cpc">Manual CPC</option>
                <option value="viewable_cpm">Viewable CPM (vCPM)</option>
                <option value="cpa">Target CPA</option>
                <option value="roas">Target ROAS</option>
              </select>
            </div>

            {/* Performance Goals */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Performance Goal</label>
              <select
                value={content.performanceGoal || 'impressions'}
                onChange={(e) => onChange('performanceGoal', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="impressions">Maximize Impressions</option>
                <option value="clicks">Maximize Clicks</option>
                <option value="conversions">Maximize Conversions</option>
                <option value="viewability">Maximize Viewability</option>
                <option value="reach">Maximize Reach</option>
              </select>
            </div>

            {/* Campaign Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Schedule</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={content.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={content.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Monthly Spend Estimate */}
            {content.budget && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Estimated Monthly Spend</h4>
                <p className="text-2xl font-bold text-blue-700">
                  ${content.budgetType === 'daily'
                    ? (content.budget * 30.4).toFixed(2)
                    : content.budgetType === 'monthly'
                    ? content.budget.toFixed(2)
                    : (content.budget / 12).toFixed(2)
                  }
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {content.budgetType === 'daily'
                    ? `Based on $${content.budget}/day × 30.4 days`
                    : content.budgetType === 'monthly'
                    ? `Monthly budget`
                    : `Total budget of $${content.budget} ÷ 12 months`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}